// src/automation/components/ScheduleEditor.js
//
// Multi-entry schedule editor. Produces a `schedules` block that the backend
// (core/scheduler.py) evaluates every 5 minutes in the task's own timezone:
//
//   { timezone: "Asia/Kolkata", days: ["mon",...],            // [] = every day
//     entries: [
//       { type: "interval", every_minutes: 60, start: "06:00", end: "18:00" },
//       { type: "times", times: ["12:30", "20:00"] },
//     ] }
//
// Entries add up; two entries due at the same minute give ONE run. Interval
// windows include the end time (06:00–18:00 hourly = 13 runs). An end earlier
// than the start crosses midnight.

import React, { useEffect, useState } from "react";
import { Plus, Trash2, Clock, Repeat } from "lucide-react";
import { previewSchedule } from "../api/automation_data";

export const TIMEZONES = [
  "Asia/Kolkata", "UTC", "Europe/London", "Europe/Dublin", "Europe/Berlin",
  "Europe/Paris", "Europe/Madrid", "Europe/Rome", "Europe/Amsterdam", "Europe/Warsaw",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "Asia/Singapore", "Asia/Tokyo", "Asia/Dubai", "Asia/Manila",
];
export const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABEL = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const MINUTE_STEPS = [5, 10, 15, 20, 30, 45];

const pad = (n) => String(n).padStart(2, "0");
const fmt = (mins) => `${pad(Math.floor(mins / 60) % 24)}:${pad(mins % 60)}`;

export function defaultSchedules() {
  return {
    timezone: "Asia/Kolkata",
    days: [],
    entries: [{ type: "interval", every_minutes: 60, start: "00:00", end: "23:59" }],
  };
}

/** Convert an old single cron(...)/rate(...) (UTC) into a `schedules` block, or null. */
export function legacyToSchedules(expr) {
  const s = String(expr ?? "").trim();
  if (!s) return null;
  const utc = (entries) => ({ timezone: "UTC", days: [], entries });
  const rate = s.match(/^rate\(\s*(\d+)\s+(minute|minutes|hour|hours|day|days)\s*\)$/i);
  if (rate) {
    const n = Number(rate[1]);
    const unit = rate[2].toLowerCase();
    let mins = n * (unit.startsWith("minute") ? 1 : unit.startsWith("hour") ? 60 : 1440);
    mins = Math.min(1440, Math.max(5, mins - (mins % 5)));
    return utc([{ type: "interval", every_minutes: mins, start: "00:00", end: "23:59" }]);
  }
  const m = s.match(/^cron\((\S+)\s+(\S+)\s+\*\s+\*\s+\?\s+\*\)$/);
  if (!m) return null;
  const [, minute, hour] = m;
  const step = minute.match(/^(\d+)\/(\d+)$/);
  if (step && hour === "*")
    return utc([{ type: "interval", every_minutes: Number(step[2]), start: fmt(Number(step[1])), end: "23:59" }]);
  if (!/^\d+$/.test(minute)) return null;
  const mi = Number(minute);
  if (hour === "*") return utc([{ type: "interval", every_minutes: 60, start: fmt(mi), end: "23:59" }]);
  const hstep = hour.match(/^(\d+)\/(\d+)$/);
  if (hstep)
    return utc([{ type: "interval", every_minutes: 60 * Number(hstep[2]), start: fmt(Number(hstep[1]) * 60 + mi), end: "23:59" }]);
  const range = hour.match(/^(\d+)-(\d+)$/);
  if (range)
    return utc([{ type: "interval", every_minutes: 60, start: fmt(Number(range[1]) * 60 + mi), end: fmt(Number(range[2]) * 60 + mi) }]);
  if (/^\d+(,\d+)*$/.test(hour))
    return utc([{ type: "times", times: hour.split(",").map((h) => fmt(Number(h) * 60 + mi)) }]);
  return null;
}

function daysLabel(days) {
  if (!days || !days.length) return "";
  const key = days.join(",");
  if (key === "mon,tue,wed,thu,fri") return "Mon–Fri";
  if (key === "sat,sun") return "Sat–Sun";
  return days.map((d) => DAY_LABEL[d] || d).join(", ");
}

function entryLabel(e) {
  if (e.type === "times") return `at ${(e.times || []).join(", ") || "—"}`;
  const n = Number(e.every_minutes) || 0;
  const every = n % 60 === 0 ? `${n / 60}h` : `${n}m`;
  const allDay = (e.start || "00:00") === "00:00" && (e.end || "23:59") === "23:59";
  return `every ${every}${allDay ? "" : ` ${e.start}–${e.end}`}${e.days?.length ? ` [${daysLabel(e.days)}]` : ""}`;
}

/** Full human summary, e.g. "every 1h 06:00–18:00 · at 12:30 (Asia/Kolkata, Mon–Fri)". */
export function describeSchedules(s) {
  if (!s || typeof s !== "object") return "—";
  const parts = (s.entries || []).filter((e) => e.enabled !== false).map(entryLabel);
  if (!parts.length) return "No runs scheduled";
  const tail = [s.timezone || "Asia/Kolkata", daysLabel(s.days)].filter(Boolean).join(", ");
  return `${parts.join(" · ")} (${tail})`;
}

/** Compact version for a KPI tile. */
export function shortSchedules(s) {
  if (!s || typeof s !== "object") return "—";
  const live = (s.entries || []).filter((e) => e.enabled !== false);
  if (!live.length) return "Not scheduled";
  if (live.length === 1) return entryLabel(live[0]).replace(/^./, (c) => c.toUpperCase());
  return `${live.length} schedules`;
}

// ── trigger-level schedules ───────────────────────────────────────────────────
// Every schedule lives on a trigger. A config saved before that may still have a
// task-level `schedules` (or an old `schedule` cron); the backend moves it into
// each trigger on the next save, so the UI shows it as the trigger's schedule.

/** {schedules, inherited} for one trigger; schedules is null = not scheduled. */
export function triggerSchedule(trig, cfg) {
  if (trig && trig.schedules) return { schedules: trig.schedules, inherited: false };
  const fallback = (cfg && cfg.schedules) || legacyToSchedules(cfg && cfg.schedule);
  return { schedules: fallback || null, inherited: !!fallback };
}

function weekdayIn(tz) {
  try {
    const short = new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: tz || "Asia/Kolkata" })
      .format(new Date())
      .toLowerCase()
      .slice(0, 3);
    return DAY_KEYS.includes(short) ? short : null;
  } catch (_) {
    return null;
  }
}

const toMin = (hhmm) => {
  const [h, m] = String(hhmm || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** Local run minutes (0..1439, plus +1440 for windows past midnight) for a weekday. */
function runMinutes(s, day) {
  const out = new Set();
  for (const e of (s && s.entries) || []) {
    if (e.enabled === false) continue;
    const days = (e.days && e.days.length ? e.days : s.days) || [];
    if (days.length && day && !days.includes(day)) continue;
    if (e.type === "times") {
      (e.times || []).forEach((t) => out.add(toMin(t)));
    } else {
      const every = Number(e.every_minutes) || 0;
      if (every <= 0) continue;
      const start = toMin(e.start || "00:00");
      let end = toMin(e.end || "23:59");
      if (end < start) end += 1440;
      for (let m = start; m <= end; m += every) out.add(m);
    }
  }
  return out;
}

/** Runs on today's weekday (in the schedule's timezone). */
export function runsPerDay(s) {
  if (!s) return 0;
  return runMinutes(s, weekdayIn(s.timezone)).size;
}

/** Task runs per day across all triggers. Triggers due at the same minute share one run. */
export function taskRunsPerDay(cfg, triggers) {
  const seen = new Set();
  for (const t of triggers || []) {
    const { schedules } = triggerSchedule(t, cfg);
    if (!schedules) continue;
    const tz = schedules.timezone || "Asia/Kolkata";
    runMinutes(schedules, weekdayIn(tz)).forEach((m) => seen.add(`${tz}|${m}`));
  }
  return seen.size;
}

// Strip editor-only keys before handing the block to the backend.
export function cleanSchedules(s) {
  if (!s || typeof s !== "object") return s;
  return {
    ...s,
    entries: (s.entries || []).map((e) => {
      const { _unit, ...rest } = e; // eslint-disable-line no-unused-vars
      return rest;
    }),
  };
}

function DayChips({ value, onChange, label }) {
  const set = new Set(value || []);
  const toggle = (d) => {
    const next = new Set(set);
    if (next.has(d)) next.delete(d);
    else next.add(d);
    const arr = DAY_KEYS.filter((k) => next.has(k));
    onChange(arr.length === 7 ? [] : arr);
  };
  return (
    <div className="auto-sched2-days" role="group" aria-label={label}>
      {DAY_KEYS.map((d) => {
        const on = set.size === 0 || set.has(d);
        return (
          <button
            key={d}
            type="button"
            className={`auto-sched2-chip ${on ? "is-on" : ""}`}
            aria-pressed={on}
            onClick={() => toggle(d)}
          >
            {DAY_LABEL[d]}
          </button>
        );
      })}
      <button type="button" className="auto-sched2-link" onClick={() => onChange([])}>Every day</button>
      <button type="button" className="auto-sched2-link" onClick={() => onChange(DAY_KEYS.slice(0, 5))}>Mon–Fri</button>
    </div>
  );
}

function IntervalEntry({ entry, onChange }) {
  const every = Number(entry.every_minutes) || 60;
  const unit = entry._unit || (every % 60 === 0 ? "hours" : "minutes");
  const allDay = (entry.start || "00:00") === "00:00" && (entry.end || "23:59") === "23:59";
  return (
    <div className="auto-sched2-row">
      <span>Every</span>
      {unit === "hours" ? (
        <input
          className="auto-eb-input auto-sched2-num"
          type="number"
          min="1"
          max="24"
          aria-label="Every how many hours"
          value={Math.max(1, Math.round(every / 60))}
          onChange={(e) => onChange({ ...entry, every_minutes: Math.min(24, Math.max(1, Number(e.target.value) || 1)) * 60 })}
        />
      ) : (
        <select
          className="auto-eb-input auto-sched2-num"
          aria-label="Every how many minutes"
          value={MINUTE_STEPS.includes(every) ? every : 30}
          onChange={(e) => onChange({ ...entry, every_minutes: Number(e.target.value) })}
        >
          {MINUTE_STEPS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      )}
      <select
        className="auto-eb-input auto-sched2-unit"
        aria-label="Interval unit"
        value={unit}
        onChange={(e) =>
          onChange({
            ...entry,
            _unit: e.target.value,
            every_minutes: e.target.value === "hours" ? 60 : 30,
          })
        }
      >
        <option value="hours">hour(s)</option>
        <option value="minutes">minutes</option>
      </select>
      {/* Run window for the day: first run at "from", last run at or before "to". */}
      <span>from</span>
      <input
        className="auto-eb-input auto-sched2-time"
        type="time"
        aria-label="First run of the day"
        value={entry.start || "00:00"}
        onChange={(e) => onChange({ ...entry, start: e.target.value || "00:00" })}
      />
      <span>to</span>
      <input
        className="auto-eb-input auto-sched2-time"
        type="time"
        aria-label="Last run of the day (included)"
        value={entry.end || "23:59"}
        onChange={(e) => onChange({ ...entry, end: e.target.value || "23:59" })}
      />
      <button
        type="button"
        className="auto-sched2-link"
        disabled={allDay}
        onClick={() => onChange({ ...entry, start: "00:00", end: "23:59" })}
      >
        All day
      </button>
      <button
        type="button"
        className="auto-sched2-link"
        onClick={() => onChange({ ...entry, start: "06:00", end: "18:00" })}
      >
        6 AM–6 PM
      </button>
      <span className="auto-eb-field-hint">
        {allDay ? "runs all day" : `first run ${entry.start || "00:00"}, last run at or before ${entry.end || "23:59"}`}
      </span>
    </div>
  );
}

function TimesEntry({ entry, onChange }) {
  const times = entry.times || [];
  const setAt = (i, v) => onChange({ ...entry, times: times.map((t, j) => (j === i ? v : t)) });
  return (
    <div className="auto-sched2-row">
      <span>At</span>
      {times.map((t, i) => (
        <span key={i} className="auto-sched2-timechip">
          <input
            className="auto-eb-input auto-sched2-time"
            type="time"
            aria-label={`Run time ${i + 1}`}
            value={t}
            onChange={(e) => setAt(i, e.target.value)}
          />
          <button
            type="button"
            className="auto-sched2-icon"
            aria-label={`Remove time ${t}`}
            onClick={() => onChange({ ...entry, times: times.filter((_, j) => j !== i) })}
          >
            ×
          </button>
        </span>
      ))}
      <button
        type="button"
        className="auto-sched2-link"
        onClick={() => onChange({ ...entry, times: [...times, "12:00"] })}
      >
        + add time
      </button>
    </div>
  );
}

export default function ScheduleEditor({ value, legacy, onChange }) {
  const converted = !value && legacy ? legacyToSchedules(legacy) : null;
  const sched = value || converted || defaultSchedules();
  const entries = sched.entries || [];
  const update = (patch) => onChange({ ...sched, ...patch });
  const setEntry = (i, e) => update({ entries: entries.map((x, j) => (j === i ? e : x)) });
  const removeEntry = (i) => update({ entries: entries.filter((_, j) => j !== i) });
  const addEntry = (type) =>
    update({
      entries: [
        ...entries,
        type === "times"
          ? { type: "times", times: ["12:30"] }
          : { type: "interval", every_minutes: 60, start: "06:00", end: "18:00" },
      ],
    });

  // Live preview of the next runs (debounced; read-only backend call).
  const [preview, setPreview] = useState({ loading: false, data: null });
  const key = JSON.stringify(cleanSchedules(sched));
  useEffect(() => {
    let alive = true;
    setPreview((p) => ({ ...p, loading: true }));
    const t = setTimeout(async () => {
      try {
        const data = await previewSchedule(JSON.parse(key));
        if (alive) setPreview({ loading: false, data });
      } catch (e) {
        if (alive) setPreview({ loading: false, data: { success: false, summary: e.message } });
      }
    }, 500);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [key]);

  const tzOptions = TIMEZONES.includes(sched.timezone) ? TIMEZONES : [sched.timezone, ...TIMEZONES];

  return (
    <div className="auto-eb-field auto-eb-field--wide auto-sched2">
      <span className="auto-eb-field-label">schedule</span>

      {legacy && !value && (
        <p className="auto-eb-field-hint auto-sched2-note">
          Current schedule is the old single rule <code>{String(legacy)}</code> (UTC).
          {converted ? " It's shown below as entries" : " It can't be shown as entries"} — saving
          switches this task to the new scheduler and turns the old rule off.
        </p>
      )}

      <div className="auto-sched2-row">
        <label className="auto-sched2-inline">
          <span>Timezone</span>
          <select
            className="auto-eb-input auto-sched2-tz"
            value={sched.timezone || "Asia/Kolkata"}
            onChange={(e) => update({ timezone: e.target.value })}
          >
            {tzOptions.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </label>
      </div>

      <div className="auto-sched2-row">
        <span>Days</span>
        <DayChips label="Run on days" value={sched.days} onChange={(days) => update({ days })} />
      </div>

      <ol className="auto-sched2-list">
        {entries.map((e, i) => (
          <li key={i} className={`auto-sched2-entry ${e.enabled === false ? "is-off" : ""}`}>
            <div className="auto-sched2-entry-head">
              <span className="auto-sched2-badge">
                {e.type === "times" ? <Clock size={12} /> : <Repeat size={12} />}
                {e.type === "times" ? "Specific times" : "Repeat"}
              </span>
              <label className="auto-sched2-inline">
                <input
                  type="checkbox"
                  checked={e.enabled !== false}
                  onChange={(ev) => {
                    const next = { ...e };
                    if (ev.target.checked) delete next.enabled;
                    else next.enabled = false;
                    setEntry(i, next);
                  }}
                />
                on
              </label>
              <label className="auto-sched2-inline">
                <input
                  type="checkbox"
                  checked={!!(e.days && e.days.length)}
                  onChange={(ev) => {
                    const next = { ...e };
                    if (ev.target.checked) next.days = DAY_KEYS.slice(0, 5);
                    else delete next.days;
                    setEntry(i, next);
                  }}
                />
                own days
              </label>
              <button
                type="button"
                className="auto-sched2-icon"
                aria-label={`Remove schedule ${i + 1}`}
                onClick={() => removeEntry(i)}
              >
                <Trash2 size={13} />
              </button>
            </div>
            {e.type === "times"
              ? <TimesEntry entry={e} onChange={(x) => setEntry(i, x)} />
              : <IntervalEntry entry={e} onChange={(x) => setEntry(i, x)} />}
            {e.days && e.days.length > 0 && (
              <div className="auto-sched2-row">
                <span>Only on</span>
                <DayChips
                  label={`Days for schedule ${i + 1}`}
                  value={e.days}
                  onChange={(days) => {
                    const next = { ...e };
                    if (days.length) next.days = days;
                    else delete next.days;
                    setEntry(i, next);
                  }}
                />
              </div>
            )}
          </li>
        ))}
      </ol>

      <div className="auto-sched2-row">
        <button type="button" className="auto-sched2-add" onClick={() => addEntry("interval")}>
          <Plus size={13} /> Repeat (hourly / every N)
        </button>
        <button type="button" className="auto-sched2-add" onClick={() => addEntry("times")}>
          <Plus size={13} /> Specific times
        </button>
      </div>

      <div className="auto-sched2-preview" aria-live="polite">
        {preview.loading && !preview.data ? (
          <span className="auto-eb-field-hint">Checking schedule…</span>
        ) : preview.data && preview.data.success === false ? (
          <span className="auto-sched2-error">{preview.data.summary}</span>
        ) : preview.data ? (
          <>
            <div className="auto-eb-field-hint">
              {preview.data.summary} · {preview.data.runs_today ?? "?"} run(s) today
            </div>
            {(preview.data.next_runs || []).length > 0 && (
              <div className="auto-sched2-next">
                Next: {(preview.data.next_runs || []).map((r) => r.local).join(" · ")}
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
